/**
 * NotifyrContext.tsx - NotifyrContext
 * 
 * CHANGE HISTORY:
 * - 251021_0543 [d4517d3] danielbeer: feat: apply performance optimizations to all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getNotifyr } from './NotifyrManager';

const NotifyrContext = createContext(null);

export const NotifyrProvider = ({ children, options = {} }) => {
  const [notifyr] = useState(() => getNotifyr(options));
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Subscribe to notification events
  useEffect(() => {
    const unsubscribe = notifyr.subscribe((event, data) => {
      // Refresh notifications on any change
      if (data.userId) {
        const userNotifications = notifyr.getNotifications(data.userId);
        setNotifications(userNotifications);
        setUnreadCount(notifyr.getUnreadCount(data.userId));
      }
    });
    
    return unsubscribe;
  }, [notifyr]);
  
  // Send notification
  const send = useCallback(async (notification) => {
    return await notifyr.send(notification);
  }, [notifyr]);
  
  // Get notifications
  const getNotifications = useCallback((userId, options) => {
    return notifyr.getNotifications(userId, options);
  }, [notifyr]);
  
  // Get unread count
  const getUnreadCount = useCallback((userId) => {
    return notifyr.getUnreadCount(userId);
  }, [notifyr]);
  
  // Mark as read
  const markAsRead = useCallback((notificationId, userId) => {
    return notifyr.markAsRead(notificationId, userId);
  }, [notifyr]);
  
  // Mark all as read
  const markAllAsRead = useCallback((userId) => {
    return notifyr.markAllAsRead(userId);
  }, [notifyr]);
  
  // Delete notification
  const deleteNotification = useCallback((notificationId, userId) => {
    return notifyr.deleteNotification(notificationId, userId);
  }, [notifyr]);
  
  // Clear all
  const clearAll = useCallback((userId) => {
    notifyr.clearAll(userId);
  }, [notifyr]);
  
  const value = useMemo(() => ({
  
    notifyr,
    notifications,
    unreadCount,
    send,
    getNotifications,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll
  }), [notifyr, notifications, unreadCount, send, getNotifications, getUnreadCount, markAsRead, markAllAsRead, deleteNotification, clearAll]);
  
  return (
    <NotifyrContext.Provider value={value}>
      {children}
    </NotifyrContext.Provider>
  );
};

export const useNotifyr = () => {
  const context = useContext(NotifyrContext);
  
  if (!context) {
    throw new Error('useNotifyr must be used within NotifyrProvider');
  }
  
  return context;
};

export default NotifyrContext;
